// Slice de UI do módulo de indicadores (MET-26: filtro global).
//
// Seis dimensões — período, loja, número de WhatsApp ("fila"), vendedor, origem
// do lead e etapa do funil — vivem AQUI e não em cada view, porque o requisito é
// que trocar de aba preserve o recorte. O `MetricsFilterBar` mora no shell, acima
// das <Tabs>: só a view troca.
//
// ⚠️ Não estender o `managementUi`: ele é lido por 4 telas, e acrescentar 4
// dimensões alargaria a cache key de `useMetricsQuery`/`useLeaderboardQuery`
// existentes, dando refetch de graça para todo mundo.
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DateRange, PeriodPreset } from '@/lib/filters/period';
import type { AuthState } from '@/features/auth';
import { selectCurrentUser } from '@/features/auth';

/** Sentinela de "sem filtro" no estado da UI. NUNCA vai para a query. */
export const ALL = 'all';

/** Dimensões filtráveis além do período. */
export type MetricsDimension = 'storeId' | 'numberId' | 'vendorId' | 'originId' | 'stageId';

export interface MetricsUiState {
  period: PeriodPreset;
  /** Não-nulo ⇒ intervalo custom ativo (sobrepõe o preset nas queries). */
  customRange: DateRange | null;
  storeId: string;
  /** "Fila" = número de WhatsApp (decisão travada com o usuário). */
  numberId: string;
  vendorId: string;
  originId: string;
  stageId: string;
}

const initialState: MetricsUiState = {
  period: '7d',
  customRange: null,
  storeId: ALL,
  numberId: ALL,
  vendorId: ALL,
  originId: ALL,
  stageId: ALL,
};

const slice = createSlice({
  name: 'metricsUi',
  initialState,
  reducers: {
    // Escolher um preset sai do modo custom (mesma semântica do managementUi).
    metricsPeriodChanged(state, action: PayloadAction<PeriodPreset>) {
      state.period = action.payload;
      state.customRange = null;
    },
    metricsCustomRangeChanged(state, action: PayloadAction<DateRange>) {
      state.customRange = action.payload;
    },
    metricsStoreChanged(state, action: PayloadAction<string>) {
      state.storeId = action.payload;
    },
    metricsNumberChanged(state, action: PayloadAction<string>) {
      state.numberId = action.payload;
    },
    metricsVendorChanged(state, action: PayloadAction<string>) {
      state.vendorId = action.payload;
    },
    metricsOriginChanged(state, action: PayloadAction<string>) {
      state.originId = action.payload;
    },
    metricsStageChanged(state, action: PayloadAction<string>) {
      state.stageId = action.payload;
    },
    /** Limpa só as dimensões; o período fica (é o que o usuário quase nunca quer zerar). */
    metricsDimensionsReset(state) {
      state.storeId = ALL;
      state.numberId = ALL;
      state.vendorId = ALL;
      state.originId = ALL;
      state.stageId = ALL;
    },
    metricsDimensionChanged(
      state,
      action: PayloadAction<{ dimension: MetricsDimension; value: string }>,
    ) {
      state[action.payload.dimension] = action.payload.value;
    },
  },
});

export const {
  metricsPeriodChanged,
  metricsCustomRangeChanged,
  metricsStoreChanged,
  metricsNumberChanged,
  metricsVendorChanged,
  metricsOriginChanged,
  metricsStageChanged,
  metricsDimensionsReset,
  metricsDimensionChanged,
} = slice.actions;
export const metricsUiReducer = slice.reducer;

type MetricsRootSlice = { metricsUi: MetricsUiState };

export const selectMetricsUi = (s: MetricsRootSlice): MetricsUiState => s.metricsUi;
export const selectMetricsPeriod = (s: MetricsRootSlice): PeriodPreset => s.metricsUi.period;
export const selectMetricsCustomRange = (s: MetricsRootSlice): DateRange | null =>
  s.metricsUi.customRange;
export const selectMetricsStore = (s: MetricsRootSlice): string => s.metricsUi.storeId;
export const selectMetricsNumber = (s: MetricsRootSlice): string => s.metricsUi.numberId;
export const selectMetricsVendor = (s: MetricsRootSlice): string => s.metricsUi.vendorId;
export const selectMetricsOrigin = (s: MetricsRootSlice): string => s.metricsUi.originId;
export const selectMetricsStage = (s: MetricsRootSlice): string => s.metricsUi.stageId;

/**
 * Argumentos que vão para a query (MET-26). Nomes dos params CONGELADOS no dia 0:
 * `period | from | to | storeId | numberId | vendorId | originId | stageId`.
 * (É `numberId`, não `waNumberId` — arbitragem da Parte 4 do plano.)
 */
export interface MetricsFilterArgs {
  period: PeriodPreset;
  from?: string;
  to?: string;
  storeId?: string;
  numberId?: string;
  vendorId?: string;
  originId?: string;
  stageId?: string;
}

/**
 * `'all'` → `undefined`, e `JSON.stringify` descarta chave `undefined`. É isso que
 * faz "sem filtro" serializar IGUAL em todas as abas e navegar entre elas
 * reaproveitar cache 100%. Regra dura: nunca mandar `''` nem `'all'` no arg — cada
 * um viraria uma segunda cache key para exatamente o mesmo dado.
 */
function omitAll(value: string | null | undefined): string | undefined {
  return value && value !== ALL ? value : undefined;
}

type UserSlice = { auth: AuthState };

/**
 * MEMOIZADO, e isso NÃO é opcional.
 *
 * Este seletor MONTA um objeto. Sem memoização ele devolve referência nova a cada
 * chamada, o `useSyncExternalStore` do react-redux entende "mudou" a cada
 * notificação da store, e todo consumidor re-renderiza a cada dispatch de qualquer
 * lugar. Com o `useEffect` do url-sync no meio, isso vira realimentação e estoura
 * em `Maximum update depth exceeded` — o defeito já documentado em
 * `features/management/slice.ts`. Aqui são 6 filtros e ~35 hooks lendo.
 *
 * O CLAMP DE RBAC (MET-27) mora aqui, e não nos componentes: `?vendedor=<outro>`
 * digitado na URL por um VENDEDOR é simplesmente ignorado, e nenhum componente
 * pode esquecer o gate — não há de onde ler o valor não-clampado.
 */
export const selectMetricsQueryArgs = createSelector(
  [selectMetricsUi, (s: UserSlice) => selectCurrentUser(s)],
  (ui, user): MetricsFilterArgs => {
    // VENDEDOR só enxerga os próprios números; o valor da URL não tem voz.
    const selfOnly = user?.role === 'VENDEDOR' || user?.role === 'ATENDENTE';
    const vendorId = selfOnly ? (user?.id ?? undefined) : omitAll(ui.vendorId);

    // Loja fora do escopo do usuário cai fora. `storeIds` vazio significa "escopo
    // desconhecido nesta sessão", não "nenhuma loja" — o back é quem decide de
    // verdade, e clampar aqui esvaziaria o painel de quem tem sessão antiga.
    const rawStore = omitAll(ui.storeId);
    const scoped = user?.storeIds ?? [];
    const storeId = rawStore && scoped.length > 0 && !scoped.includes(rawStore) ? undefined : rawStore;

    return {
      period: ui.period,
      // both-or-neither: mandar só um lado faz o back cair no preset, e o número
      // exibido deixa de bater com o filtro que está na tela.
      ...(ui.customRange ? { from: ui.customRange.from, to: ui.customRange.to } : {}),
      ...(storeId ? { storeId } : {}),
      ...(omitAll(ui.numberId) ? { numberId: omitAll(ui.numberId) } : {}),
      ...(vendorId ? { vendorId } : {}),
      ...(omitAll(ui.originId) ? { originId: omitAll(ui.originId) } : {}),
      ...(omitAll(ui.stageId) ? { stageId: omitAll(ui.stageId) } : {}),
    };
  },
);

/** Quantas dimensões (fora o período) estão ativas — alimenta o "Limpar filtros". */
export const selectMetricsActiveDimensionCount = createSelector([selectMetricsUi], (ui) =>
  [ui.storeId, ui.numberId, ui.vendorId, ui.originId, ui.stageId].filter((v) => v !== ALL).length,
);
